import { useEffect, useState } from 'react';
import type { Project } from '@obraplan/core/domain/structure/project';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, Input } from '@/components/ui/input';

/**
 * "Duplicar obra" (07/10/2026): cria uma versão de orçamento com o projeto e o orçamento da
 * obra escolhida — estrutura, desenhos, peças, serviços, índices e quantitativo —, sem
 * nenhuma execução. O nome vem sugerido (`versionName`) e é livre: a versão se renomeia
 * depois, como qualquer obra.
 *
 * Depois de criada, o diálogo mostra a versão nova e oferece abri-la — selecioná-la como a
 * obra ativa —, que é o que quem duplica quer fazer em seguida.
 */
export function DuplicateProjectDialog({
  open,
  onOpenChange,
  source,
  suggestedName,
  onSubmit,
  isPending,
  errorMessage,
  created,
  onOpenCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** A obra copiada. */
  source: Project | undefined;
  suggestedName: string;
  onSubmit: (name: string) => void;
  isPending: boolean;
  errorMessage: string | undefined;
  /** A versão que acabou de nascer: troca o formulário pelo aviso de pronto. */
  created: Project | undefined;
  onOpenCreated: (project: Project) => void;
}) {
  const [name, setName] = useState(suggestedName);

  // Cada abertura recomeça da sugestão: o que foi digitado numa obra não vaza para outra.
  useEffect(() => {
    if (open) setName(suggestedName);
  }, [open, suggestedName, source?.id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        {created === undefined ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              onSubmit(name.trim());
            }}
            className="flex flex-col gap-4"
          >
            <DialogHeader>
              <DialogTitle>Duplicar obra</DialogTitle>
              <DialogDescription>
                Cria uma versão de orçamento de {source?.name ?? 'a obra'}, com o mesmo projeto e
                orçamento. Edite à vontade: a execução continua na obra aprovada.
              </DialogDescription>
            </DialogHeader>
            <Field label="Nome da versão" htmlFor="duplicate-project-name" required>
              <Input
                id="duplicate-project-name"
                autoFocus
                value={name}
                maxLength={160}
                onChange={(event) => setName(event.target.value)}
              />
            </Field>
            {errorMessage !== undefined && (
              <p role="alert" className="text-sm text-(--color-state-danger)">
                {errorMessage}
              </p>
            )}
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" variant="primary" disabled={isPending || name.trim() === ''}>
                {isPending ? 'Duplicando…' : 'Duplicar'}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="flex flex-col gap-4">
            <DialogHeader>
              <DialogTitle>Versão criada</DialogTitle>
              <DialogDescription>
                {created.name} já está na lista, logo abaixo da obra aprovada. Abra para editar o
                orçamento.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
                Fechar
              </Button>
              <Button type="button" variant="primary" onClick={() => onOpenCreated(created)}>
                Abrir a versão
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
